import React from "react";
import { CheckIcon } from "lucide-react";
import styled from "styled-components/macro";

import VisuallyHidden from "../../../components/VisuallyHidden";
import { Tray, TrayCells, TrayHeader } from "../../../probate/components/tray";
import { formatDateTime } from "../../../utils/textUtils";

// The steps take the sizes, spacing and connecting line of Mark's numbered TrayCell, but they
// aren't links, so they have no chevron and aren't TrayCells

const cellPaddingBlock = "var(--tray-cell-padding-block, var(--juno-space-4))";
const inset = "var(--tray-inset, 0px)";
const markerSize = "var(--juno-space-7)";

// The design's one card that fills the column, where ProbateLayout insets the rest
const StatusTray = styled(Tray)`
  margin-right: calc(-1 * var(--probate-card-inset, 0px));
  margin-left: calc(-1 * var(--probate-card-inset, 0px));
`;

// The steps carry the tray's inset themselves, so a step in progress can fill to the card's padding
const Steps = styled(TrayCells)`
  margin-right: calc(-1 * ${inset});
  margin-left: calc(-1 * ${inset});
`;

const Step = styled.li`
  position: relative;
  display: flex;
  gap: var(--juno-space-4);
  padding: 0 ${inset};

  /* The design's current steps: a highlighted 24px shape, concentric with the card */
  &[data-status="started"] {
    border-radius: calc(var(--tray-radius, var(--radius-xl)) - var(--tray-padding, var(--juno-space-6)));
    background-color: var(--highlight);
    color: var(--highlight-foreground);
  }

  &[data-status="started"] + &[data-status="started"] {
    margin-top: var(--juno-space-1);
  }
`;

// Centres the 1px line under the marker
const Line = styled.span`
  position: absolute;
  left: calc(${inset} + ${markerSize} / 2 - 0.5px);
  width: 1px;
  background-color: var(--foreground);

  ${Step}[data-status="started"] & {
    display: none;
  }
`;

const LineIn = styled(Line)`
  top: 0;
  height: ${cellPaddingBlock};

  ${Step}:first-child & {
    display: none;
  }
`;

const LineOut = styled(Line)`
  top: calc(${cellPaddingBlock} + ${markerSize});
  bottom: 0;

  ${Step}:last-child & {
    display: none;
  }
`;

const Marker = styled.span`
  box-sizing: border-box;
  display: grid;
  width: ${markerSize};
  height: ${markerSize};
  flex-shrink: 0;
  place-items: center;
  margin-top: ${cellPaddingBlock};
  border-radius: 50%;
  font-size: var(--text-sm);
  line-height: 1;
  font-weight: var(--juno-font-weight-bold);

  &[data-status="complete"] {
    background-color: var(--primary);
    color: var(--primary-foreground);
  }

  /* The design canvas's ring, at Mark's 1.5px focus ring weight */
  &[data-status="started"],
  &[data-status="not_started"] {
    border: 1.5px solid var(--foreground);
    /* Overpass draws digits 0.1em above the centre, as with the badge in probate/theme.css */
    padding-top: 0.2em;
  }

  svg {
    width: var(--juno-space-4);
    height: var(--juno-space-4);
  }
`;

// The divider sits above each step, so none runs into or out of a step in progress
const Body = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: var(--juno-space-1);
  padding: ${cellPaddingBlock} 0;
  border-top: 1px solid var(--border);

  li:first-child > &,
  li[data-status="started"] > &,
  li[data-status="started"] + li > & {
    border-top: 0;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: var(--tray-cell-title-font-size, var(--text-lg));
  line-height: var(--tray-cell-title-line-height, var(--text-lg--line-height));
  font-weight: var(--juno-font-weight-regular);
`;

const Description = styled.p`
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);
  color: var(--muted-foreground);

  ${Step}[data-status="started"] & {
    color: var(--highlight-muted-foreground);
  }
`;

// The design's summary line: a tick on a complete step, then the label and the first description
const Summary = styled(Description)`
  display: flex;
  gap: var(--juno-space-2);

  color: inherit;

  /* && outranks Description's highlight rule, so the label keeps the row's text colour */
  li[data-status="started"] && {
    color: inherit;
  }

  svg {
    width: var(--juno-space-4);
    height: var(--juno-space-4);
    flex-shrink: 0;
    margin-top: calc((var(--text-sm--line-height) - var(--juno-space-4)) / 2);
  }
`;

const SummaryDescription = styled.span`
  color: var(--muted-foreground);

  ${Step}[data-status="started"] & {
    color: var(--highlight-muted-foreground);
  }
`;

const Updated = styled.p`
  margin: 0;
  padding: var(--juno-space-2) 0;
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);
`;

const Descriptions = ({ section }) => {
  const descriptions = section.descriptions ?? [];

  if (!section.status_label) {
    return descriptions.map((description, index) => <Description key={index}>{description}</Description>);
  }

  const [first, ...rest] = descriptions;
  return (
    <>
      <Summary>
        {section.status === "complete" && <CheckIcon aria-hidden />}
        <span>
          {section.status_label}
          {first != null && (
            <>
              {" "}
              <SummaryDescription>{first}</SummaryDescription>
            </>
          )}
        </span>
      </Summary>
      {rest.map((description, index) => (
        <Description key={index}>{description}</Description>
      ))}
    </>
  );
};

const ProbateStatus = ({ selectedCase }) => {
  const statusUpdate = selectedCase.status_updates?.client_dashboard_overview;

  if (!statusUpdate) {
    return null;
  }

  return (
    <StatusTray variant="card" headingId="current-status">
      <TrayHeader title="Your case progress" />
      <Steps>
        {statusUpdate.sections.map((section, index) => (
          <Step key={section.identifier ?? section.title} data-status={section.status}>
            <LineIn aria-hidden />
            <LineOut aria-hidden />
            <Marker data-status={section.status} aria-hidden>
              {section.status === "complete" ? <CheckIcon /> : index + 1}
            </Marker>
            <Body>
              <Title>
                {section.title}
                {section.status === "not_started" && <VisuallyHidden>, not started</VisuallyHidden>}
              </Title>
              <Descriptions section={section} />
            </Body>
          </Step>
        ))}
      </Steps>
      <Updated>Status updated: {formatDateTime(statusUpdate.last_updated)}</Updated>
    </StatusTray>
  );
};

export default ProbateStatus;
